import { z } from 'zod'

/**
 * Research enabled parameter for agent/assistant request params
 */
export const LRRSearchFilterSchema = z.object({
  jurisdiction_codes: z.array(z.string()),
})

export type LRRSearchFilter = z.infer<typeof LRRSearchFilterSchema>

export const CaselawSearchFilterSchema = z.object({
  jurisdiction_codes: z.array(z.string()),
  date_low: z.string().datetime({ message: 'Invalid date format. Expected ISO 8601 datetime string' }).nullable().optional(),
  date_high: z.string().datetime({ message: 'Invalid date format. Expected ISO 8601 datetime string' }).nullable().optional(),
  include_unpublished_cases: z.boolean().default(false),
  year_low: z.number().nullable().optional(), // backwards compatibility for legacy conversations
  year_high: z.number().nullable().optional(), // backwards compatibility for legacy conversations
})

export type CaselawSearchFilter = z.infer<typeof CaselawSearchFilterSchema>

export const UserFilesRAGRequestSchema = z.object({
  user_file_ids: z.array(z.string()).default([]),
})

export type UserFilesRAGRequest = z.infer<typeof UserFilesRAGRequestSchema>

export const WritingSampleFilesRequestSchema = z.object({
  user_file_ids: z.array(z.string()).default([]),
})

export type WritingSampleFilesRequest = z.infer<typeof WritingSampleFilesRequestSchema>

/** What an uploaded or selected file attaches to: the multi-file user_files (RAG) field, or the single writing sample. */
export type FileSelectionTarget = 'user-files' | 'writing-sample'

/**
 * Agent Assistant Request Params
 * This schema is a replication of the backend agent assistant
 * form data model, used for processing requests.
 *
 * Default values are provided for each field.
 */
export const AgentAssistantRequestParamsSchema = z.object({
  input_text: z.string().default(''),
  user_files_rag: UserFilesRAGRequestSchema.nullish().default(null),
  file_paths: z.array(z.string()).default([]),
  // Legacy writing-sample GCS paths (legacy brands). New files UX sends writing_sample_files instead.
  template_paths: z.array(z.string()).default([]),
  // New user_files writing-sample payload (new files UX). Mutually exclusive with template_paths.
  writing_sample_files: WritingSampleFilesRequestSchema.nullish().default(null),
  caselaw_search_filter: CaselawSearchFilterSchema.nullish(),
  lrr_search_filter: LRRSearchFilterSchema.nullish(),
})

export type AgentAssistantRequestParams = z.infer<typeof AgentAssistantRequestParamsSchema>
